import { Flex, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import React, { useContext } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { RxCornerBottomLeft } from 'react-icons/rx';
import { Link } from 'react-router-dom';
import { GlobalContext } from '../global-context';
import { CrmType, getCrmUrl } from '../utils/CrmUtils';
import './ExportResultsTable.scss';
import LoadingBars from './LoadingBars';
import NA from './NA';
import UniversalTag from './UniversalTag';

interface ExportResultChildRecord {
  id_source: string;
  record_type: string;
  data: Record<string, unknown>;
  operation?: string;
  id_crm?: string;
  export_status?: string;
  export_error?: string;
  user_decision?: string | null;
}

interface ExportResultRecord {
  id_source: string;
  record_type: string;
  data: Record<string, unknown>;
  operation?: string;
  id_crm?: string;
  export_status?: string;
  export_error?: string;
  user_decision?: string | null;
  child_records?: ExportResultChildRecord[];
}

interface ExportResultsTableProps {
  records: ExportResultRecord[];
  crmType: 'HUBSPOT' | 'SALESFORCE';
  hubspotPortalId?: string;
  salesforceInstanceUrl?: string;
  entityType: 'CONTACT' | 'INSTITUTION';
  salesforceObject?: 'Contact' | 'Lead';
  isLoading?: boolean;
}

function getFirstMappedValue(data: Record<string, unknown>): string | null {
  const values = Object.values(data);
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function getRecordName(record: ExportResultRecord): string {
  const data = record.data;

  if (record.record_type === 'CONTACT') {
    // Default: email
    const email = data.email as string;
    if (email) return email;

    // Fallback: first mapped value
    const firstValue = getFirstMappedValue(data);
    if (firstValue) return firstValue;

    // Last resort: truncated ID
    return `Contact ${record.id_source.slice(0, 8)}...`;
  }

  // Institution - default: name
  const name = data.name as string;
  if (name) return name;

  // Fallback: first mapped value
  const firstValue = getFirstMappedValue(data);
  if (firstValue) return firstValue;

  // Last resort: truncated ID
  return `Institution ${record.id_source.slice(0, 8)}...`;
}


export default function ExportResultsTable({
  records,
  crmType,
  hubspotPortalId,
  salesforceInstanceUrl,
  entityType,
  salesforceObject,
  isLoading
}: ExportResultsTableProps) {
  const globalContext = useContext(GlobalContext);

  const getEduleadUrl = (record: ExportResultRecord): string => {
    if (record.record_type === 'CONTACT') {
      return `/directory/${globalContext.environment.entities.contact.urlSegment}/${record.id_source}`;
    }
    return `/directory/institutions/${record.id_source}`;
  };

  if (isLoading) {
    return (
      <div className="export-results-table">
        <LoadingBars numberOfRows={5} />
      </div>
    );
  }

  if (records.length === 0) {
    return null;
  }

  const getCrmEntityType = (recordType: string) => {
    if (recordType === 'CONTACT') {
      // For Salesforce, use the salesforceObject to determine if it's a Lead or Contact
      if (crmType === 'SALESFORCE' && salesforceObject === 'Lead') {
        return 'lead';
      }
      return 'contact';
    }
    return 'company';
  };

  return (
    <div className="export-results-table">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell as="th"></TableCell>
            <TableCell as="th">Type</TableCell>
            <TableCell as="th">Decision</TableCell>
            <TableCell as="th">Operation</TableCell>
            <TableCell as="th">Status</TableCell>
            <TableCell as="th">Edulead</TableCell>
            <TableCell as="th">{crmType === 'HUBSPOT' ? 'HubSpot' : 'Salesforce'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((record) => {
            const crmUrl = getCrmUrl(
              crmType as CrmType,
              getCrmEntityType(record.record_type),
              record.id_crm,
              { hubspotPortalId, salesforceInstanceUrl }
            );

            return (
              <React.Fragment key={record.id_source}>
                <TableRow>
                  <TableCell>{getRecordName(record)}</TableCell>
                  <TableCell>
                    <UniversalTag category={record.record_type} classOverride="tag-teal" />
                  </TableCell>
                  <TableCell>
                    {record.user_decision && (
                      <UniversalTag category={record.user_decision} />
                    )}
                  </TableCell>
                  <TableCell>
                    {record.operation && (
                      <UniversalTag category={record.operation} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Flex gap={8} alignItems="center">
                      {record.export_status ? (
                        <span
                          data-tooltip-hidden={!record.export_error}
                          data-tooltip-id="main-tooltip"
                          data-tooltip-content={record.export_error}
                        >
                          <UniversalTag category={record.export_status} />
                        </span>
                      ) : (
                        <NA />
                      )}
                    </Flex>
                  </TableCell>
                  <TableCell>
                    <Link to={getEduleadUrl(record)}>
                      View <FaExternalLinkAlt size={10} />
                    </Link>
                  </TableCell>
                  <TableCell>
                    {crmUrl ? (
                      <a href={crmUrl} target="_blank" rel="noopener noreferrer">
                        View <FaExternalLinkAlt size={10} />
                      </a>
                    ) : (
                      <NA />
                    )}
                  </TableCell>
                </TableRow>
                {/* Child records (associations or child institutions) */}
                {record.child_records?.map((child) => {
                  const childData = child.data as Record<string, unknown>;
                  const isChildInstitution = child.record_type === 'INSTITUTION';

                  // For child institutions, use institution data directly
                  // For associations, use the association data format
                  const displayName = isChildInstitution
                    ? (childData?.name as string)
                    : (childData?.institution_name as string);
                  const displayType = isChildInstitution
                    ? (childData?.type as string)?.toLowerCase()
                    : (childData?.institution_type as string)?.toLowerCase();

                  // Build Edulead URL for child institutions
                  const childEduleadUrl = isChildInstitution
                    ? `/directory/${displayType === 'school' ? 'schools' : 'districts'}/${child.id_source}`
                    : null;

                  // Build CRM URL for child institutions
                  const childIdCrm = isChildInstitution ? child.id_crm : null;
                  const childCrmUrl = childIdCrm
                    ? getCrmUrl(crmType as CrmType, 'company', childIdCrm, { hubspotPortalId, salesforceInstanceUrl })
                    : null;

                  return (
                    <TableRow key={child.id_source} className="export-results-table__child-row">
                      <TableCell>
                        <span className="export-results-table__child-connector"><RxCornerBottomLeft /></span>
                        {displayName || <NA />}
                      </TableCell>
                      <TableCell>
                        {isChildInstitution && (
                          <UniversalTag category={child.record_type} classOverride="tag-teal" />
                        )}
                      </TableCell>
                      <TableCell>
                        {child.user_decision && (
                          <UniversalTag category={child.user_decision} />
                        )}
                      </TableCell>
                      <TableCell>
                        {child.operation && (
                          <UniversalTag category={child.operation} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Flex gap={8} alignItems="center">
                          {child.export_status ? (
                            <span
                              data-tooltip-hidden={!child.export_error}
                              data-tooltip-id="main-tooltip"
                              data-tooltip-content={child.export_error}
                            >
                              <UniversalTag category={child.export_status} />
                            </span>
                          ) : (
                            <NA />
                          )}
                        </Flex>
                      </TableCell>
                      <TableCell>
                        {childEduleadUrl ? (
                          <Link to={childEduleadUrl}>
                            View <FaExternalLinkAlt size={10} />
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {childCrmUrl ? (
                          <a href={childCrmUrl} target="_blank" rel="noopener noreferrer">
                            View <FaExternalLinkAlt size={10} />
                          </a>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
